import * as React from "react";
import PropTypes from "prop-types";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import DeleteIcon from "@mui/icons-material/Delete";
import FilterListIcon from "@mui/icons-material/FilterList";
import { Button, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import noDataFolder from "../../Assets/folder.png";
import { useMemo } from "react";
import { TableRowSkeleton } from "../../components/adminComponents/SimulationActivity";

function createData(id, name, calories, fat, carbs, protein) {
  return {
    id,
    name,
    calories,
    fat,
    carbs,
    protein,
  };
}

const TableDataStyles = {
  fontSize: "15px",
  color: "#002147",
};

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const headCells = [
  {
    id: "id",
    numeric: false,
    disablePadding: true,
    label: "S.No",
  },
  {
    id: "name",
    numeric: true,
    disablePadding: false,
    label: "Name",
  },
  {
    id: "Institution",
    numeric: true,
    disablePadding: false,
    label: "Institution Name",
  },
  {
    id: "email",
    numeric: true,
    disablePadding: false,
    label: "Email Address",
  },
  {
    id: "phone",
    numeric: true,
    disablePadding: false,
    label: "Phone Number",
  },
  {
    id: "memberCount",
    numeric: true,
    disablePadding: false,
    label: "Members Count",
  },
  {
    id: "action",
    numeric: true,
    disablePadding: false,
    label: "Action",
  },
];

function EnhancedTableHead(props) {
  const {
    order,
    onSelectAllClick,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead
      sx={{
        "& th": {
          backgroundColor: "#51b0cfff",
          color: "white",
        },
      }}
    >
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.id === "action" ? "center" : "left"}
            sx={{
              verticalAlign: "middle",
            }}
          >
            <Box
              sx={{
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: 1.3,
                textAlign: headCell.id === "action" ? "center" : "left",
              }}
            >
              {headCell.label}
            </Box>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

function EnhancedTableToolbar(props) {
  const { numSelected } = props;
  return (
    <Toolbar
      sx={[
        {
          pl: { sm: 2 },
          pr: { xs: 1, sm: 1 },
        },
        numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(
              theme.palette.primary.main,
              theme.palette.action.activatedOpacity,
            ),
        },
      ]}
    >
      {numSelected > 0 ? (
        <Typography
          sx={{ flex: "1 1 100%" }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {numSelected} selected
        </Typography>
      ) : (
        <Typography
          sx={{ flex: "1 1 100%" }}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          Nutrition
        </Typography>
      )}
      {numSelected > 0 ? (
        <Tooltip title="Delete">
          <IconButton>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Filter list">
          <IconButton>
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      )}
    </Toolbar>
  );
}

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

const AdminTableData = (props) => {
  let { allAdminDetailsData, allAdminLoading } = props;

  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("calories");
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [dense, setDense] = React.useState(false);
  const [rowsPerPage, setRowsPerPage] = React.useState(20);

  const navigate = useNavigate();

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = allAdminDetailsData?.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    tableRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    tableRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleChangeDense = (event) => {
    setDense(event.target.checked);
  };

  const emptyRows =
    page > 0
      ? Math.max(0, (1 + page) * rowsPerPage - allAdminDetailsData?.length)
      : 0;

  const arr = Array.isArray(allAdminDetailsData) ? allAdminDetailsData : [];
  const visibleRows = useMemo(
    () =>
      [...arr]
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, allAdminDetailsData],
  );

  const tableRef = React.useRef(null);

  return (
    <Box>
      <Paper sx={{ width: "100%", mb: 2 }}>
        <TableContainer
          sx={{
            height: 400,
            scrollbarWidth: "thin",
          }}
        >
          <Table
            stickyHeader
            aria-labelledby="tableTitle"
            size={dense ? "small" : "medium"}
            ref={tableRef}
            sx={{
              overflowX: "scroll",
            }}
          >
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={allAdminDetailsData?.length}
            />
            <TableBody>
              {allAdminLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRowSkeleton key={i} />
                ))
              ) : !visibleRows.length ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Stack
                      height={296}
                      textAlign="center"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <img
                        src={noDataFolder}
                        height={"100px"}
                        width={"100px"}
                        alt="img"
                      />
                      <Typography>No data found</Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : (
                visibleRows.map((row, index) => {
                  const isItemSelected = selected.includes(row.id);
                  const labelId = `enhanced-table-checkbox-${index}`;
                  const rowNumber = page * rowsPerPage + index + 1;

                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={row.id}
                      selected={isItemSelected}
                      sx={{ cursor: "pointer", overflow: "scroll" }}
                    >
                      <TableCell
                        component="th"
                        id={labelId}
                        scope="row"
                        padding="normal"
                        sx={{
                          TableDataStyles,
                          whiteSpace: "nowrap",
                          textTransform: "capitalize",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          width: 70,
                        }}
                      >
                        {rowNumber}
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{
                          ...TableDataStyles,
                          whiteSpace: "nowrap",
                          textTransform: "capitalize",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: 150, // you can adjust this width
                        }}
                      >{`${row.first_name ?? "-"}${" "}${
                        row.last_name ?? "-"
                      }`}</TableCell>
                      <TableCell
                        align="left"
                        sx={{
                          ...TableDataStyles,
                          whiteSpace: "nowrap",
                          textTransform: "capitalize",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: 150, // you can adjust this width
                        }}
                      >
                        {row?.institutions[0]?.institutions_name ?? "-"}
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{
                          ...TableDataStyles,
                          whiteSpace: "nowrap",
                          // textTransform: "capitalize",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: 300, // you can adjust this width
                        }}
                      >
                        {row.email_id ?? "-"}
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{
                          ...TableDataStyles,
                          whiteSpace: "nowrap",
                          textTransform: "capitalize",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: 250, // you can adjust this width
                        }}
                      >
                        {row.mobile_number}
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{
                          ...TableDataStyles,
                          whiteSpace: "nowrap",
                          textTransform: "capitalize",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: 100, // you can adjust this width
                        }}
                      >
                        {row?.institutions[0]?.members_count ?? "-"}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          onClick={() =>
                            navigate("/superAdmin/adminControls/profile", {
                              state: row.user_id,
                            })
                          }
                          size="small"
                          variant="contained"
                          sx={{
                            fontSize: "14px",
                            letterSpacing: 1,
                            background: "transparent",
                            color: "#33436aff",
                            fontWeight: 600,
                            ":hover": {
                              color: "#33436aff",
                              background: "transparent",
                            },
                          }}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: (dense ? 33 : 53) * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[20, 40, 60]}
          component="div"
          count={allAdminDetailsData?.length ?? 0}
          rowsPerPage={rowsPerPage ?? 0}
          page={page ?? 0}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      <FormControlLabel
        control={
          <Switch
            color="#51b0cf"
            checked={dense}
            onChange={handleChangeDense}
          />
        }
        label="Compact View"
      />
    </Box>
  );
};
export default AdminTableData;
